import React, { useEffect } from 'react';
import { useUserConfigState, useUserConfigDispatch } from '@/context/UserConfigContext';
import { MealBreakConfigForm } from '@/components/config_forms/meal_break/MealBreakConfigForm';
import { toast } from 'sonner';
import { useOptionalFieldValidation } from '@/components/validation';
import { useDefaultExtendedTiers } from '@/hooks/useDefaultExtendedTiers';
import type { MealBreakConfig as TMealBreakConfig, UserConfigurationState } from '@/types';
import { cn } from '@/lib/utils';

interface UserMealBreakConfigProps {
  noCard?: boolean;
  roles?: UserConfigurationState['roles'];
  rateMultipliers?: UserConfigurationState['rateMultipliers'];
}

const roundToNearest5 = (value: number | null): number | null => {
  if (value === null || isNaN(value)) return null;
  return Math.round(value / 5) * 5;
};

export const MealBreakConfig: React.FC<UserMealBreakConfigProps> = ({
  noCard,
  roles: propRoles,
  rateMultipliers: propRateMultipliers,
}) => {
  const state = useUserConfigState();
  const { updateBillingConfig } = useUserConfigDispatch();
  const defaultExtendedTiers = useDefaultExtendedTiers('meal');

  const effectiveMultipliers = propRateMultipliers ?? state.rateMultipliers ?? [];
  const hasNonStraightTimeMultipliers = effectiveMultipliers.some(m => m.code !== 'STRAIGHT_TIME');

  const mealConfig = state.mealConfig || {
    applyMealSurcharge: false,
    mealDeadlineHours: null,
    recurringMealDeadline: false,
    gracePeriodMinutes: null,
    breakBillingPolicy: null,
    mealSurchargeType: null,
    useAdditionalOffset: true,
    creditIntervalMinutes: null,
    stepUpAfterMinutes: null,
    timeCreditMinutes: null,
    fixedFeeSchedule: null,
    fixedFeeAmount: null,
    premiumType: null,
    premiumValue: null,
    premiumBasis: 'STRAIGHT_TIME',
    premiumStacking: 'flat' as const,
    applyOnHalfDays: true,
    applicableRoleIds: null,
  };

  const onMealConfigChange = (updates: Partial<TMealBreakConfig>) => {
    updateBillingConfig(prevState => {
      const currentMealConfig = prevState.mealConfig || {};
      const newUpdates: Partial<TMealBreakConfig> = { ...updates };
      if (updates.mealSurchargeType === 'hourly_premium' && !(currentMealConfig as TMealBreakConfig).premiumType) {
        newUpdates.premiumType = 'fixed';
      }
      const finalConfig: TMealBreakConfig = { ...currentMealConfig, ...newUpdates } as TMealBreakConfig;
      return { mealConfig: finalConfig } as Partial<UserConfigurationState>;
    });

    if (updates.applyMealSurcharge !== undefined) {
      toast.success(updates.applyMealSurcharge ? "Meal surcharge enabled" : "Meal surcharge disabled");
    }
  };

  useEffect(() => {
    if (mealConfig.applyMealSurcharge && mealConfig.mealSurchargeType === 'offset_overtime' && !hasNonStraightTimeMultipliers) {
      onMealConfigChange({ mealSurchargeType: null });
      toast.info('Meal surcharge method reset: "Offset Overtime" is unavailable without overtime multipliers.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onMealConfigChange is defined in render scope
  }, [hasNonStraightTimeMultipliers, mealConfig.applyMealSurcharge, mealConfig.mealSurchargeType]);

  // Field validations via ValidationProvider schemas
  const threshold = useOptionalFieldValidation('mealConfig.mealDeadlineHours');
  const grace = useOptionalFieldValidation('mealConfig.gracePeriodMinutes');
  const cadence = useOptionalFieldValidation('mealConfig.creditIntervalMinutes');
  const escalation = useOptionalFieldValidation('mealConfig.stepUpAfterMinutes');
  const creditTime = useOptionalFieldValidation('mealConfig.timeCreditMinutes');
  const halfDayBreaks = useOptionalFieldValidation('mealConfig.requiredBreaksHalfDay');
  const premiumVal = useOptionalFieldValidation('mealConfig.premiumValue');

  const buildInputProps = (
    fieldKey: string,
    value: number | null | undefined,
    onChange: (value: number | null) => void,
    validation: ReturnType<typeof useOptionalFieldValidation>,
  ): Record<string, unknown> => ({
    'data-validation-key': fieldKey,
    value: value ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value ? parseFloat(e.target.value) : null;
      onChange(v);
    },
    onBlur: validation.onBlur,
    className: cn(validation.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
  });

  const thresholdInputProps = buildInputProps(
    'mealConfig.mealDeadlineHours',
    mealConfig.mealDeadlineHours,
    (v) => onMealConfigChange({ mealDeadlineHours: v }),
    threshold,
  );

  const graceInputProps = buildInputProps(
    'mealConfig.gracePeriodMinutes',
    mealConfig.gracePeriodMinutes,
    (v) => onMealConfigChange({ gracePeriodMinutes: v }),
    grace,
  );

  const cadenceInputProps = buildInputProps(
    'mealConfig.creditIntervalMinutes',
    mealConfig.creditIntervalMinutes,
    (v) => onMealConfigChange({ creditIntervalMinutes: v }),
    cadence,
  );

  // Rounding blur handler for cadence
  const createRoundingBlurHandler = (
    originalOnBlur: () => void,
    onChangeHandler: (value: number | null) => void,
    roundingMessage: string
  ) => (e: React.FocusEvent<HTMLInputElement>) => {
    const numValue = e.target.value ? parseFloat(e.target.value) : null;
    const rounded = roundToNearest5(numValue);
    if (rounded !== numValue && numValue !== null) {
      onChangeHandler(rounded);
      setTimeout(() => {
        toast.success(roundingMessage.replace('${v}', rounded!.toString()));
      }, 0);
    }
    originalOnBlur();
  };

  const creditIntervalMinutesProps = {
    ...cadenceInputProps,
    onBlur: createRoundingBlurHandler(
      cadence.onBlur,
      (v) => onMealConfigChange({ creditIntervalMinutes: v }),
      'Surcharge cadence rounded to ${v} minutes (nearest 5-minute interval)'
    ),
  };

  const escalationInputProps = buildInputProps(
    'mealConfig.stepUpAfterMinutes',
    mealConfig.stepUpAfterMinutes,
    (v) => onMealConfigChange({ stepUpAfterMinutes: v }),
    escalation,
  );

  const creditTimeInputProps = buildInputProps(
    'mealConfig.timeCreditMinutes',
    mealConfig.timeCreditMinutes,
    (v) => onMealConfigChange({ timeCreditMinutes: v }),
    creditTime,
  );

  const halfDayBreaksInputProps = buildInputProps(
    'mealConfig.requiredBreaksHalfDay',
    mealConfig.requiredBreaksHalfDay,
    (v) => onMealConfigChange({ requiredBreaksHalfDay: v }),
    halfDayBreaks,
  );

  const premiumValueInputProps = buildInputProps(
    'mealConfig.premiumValue',
    mealConfig.premiumValue,
    (v) => onMealConfigChange({ premiumValue: v }),
    premiumVal,
  );

  return (
    <MealBreakConfigForm
      title="Meal Break Rules"
      description="Define your standard rules and surcharges for meal breaks. These settings will apply to all clients unless overridden."
      mealConfig={mealConfig}
      context="user"
      onMealConfigChange={onMealConfigChange}
      hideToggle={true}
      mealDeadlineHoursProps={thresholdInputProps}
      mealDeadlineHoursError={threshold.errorElement}
      gracePeriodMinutesProps={graceInputProps}
      gracePeriodMinutesError={grace.errorElement}
      creditIntervalMinutesProps={creditIntervalMinutesProps}
      creditIntervalMinutesError={cadence.errorElement}
      stepUpAfterMinutesProps={escalationInputProps}
      stepUpAfterMinutesError={escalation.errorElement}
      timeCreditMinutesProps={creditTimeInputProps}
      timeCreditMinutesError={creditTime.errorElement}
      premiumValueProps={premiumValueInputProps}
      premiumValueError={premiumVal.errorElement}
      halfDayBreaksProps={halfDayBreaksInputProps}
      halfDayBreaksError={halfDayBreaks.errorElement}
      roles={propRoles ?? state.roles}
      rateMultipliers={propRateMultipliers ?? state.rateMultipliers ?? []}
      minimumBillableHours={state.billingStructureConfig?.minimumBillableHours}
      halfDayHours={state.billingStructureConfig?.halfDayHours}
      noCard={noCard}
      extendedOvertimeEnabled={state.mealConfig?.extendedOvertimeEnabled ?? false}
      onExtendedOvertimeEnabledChange={(v) => onMealConfigChange({ extendedOvertimeEnabled: v })}
      extendedOvertimeTiers={state.mealConfig?.extendedOvertimeTiers ?? []}
      onExtendedOvertimeTiersChange={(tiers) => onMealConfigChange({ extendedOvertimeTiers: tiers })}
      defaultExtendedTiers={defaultExtendedTiers}
    />
  );
};
